import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Archive, Bell, Calculator, CalendarDays, Clock3, Download, FileCheck2, Fingerprint,
  History, LayoutDashboard, LogOut, SlidersHorizontal, Users,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fechaLargaBA, horaBA, useAhoraBA } from "@/components/ClockBA";

const MENUS = {
  rrhh: [
    { to: "/rrhh/hoy", label: "Hoy", icon: LayoutDashboard, badge: true },
    { to: "/rrhh/alertas", label: "Alertas", icon: Bell },
    { to: "/rrhh/fichadas", label: "Fichadas", icon: Fingerprint },
    { to: "/rrhh/justificativos", label: "Justificativos", icon: FileCheck2 },
    { to: "/rrhh/empleados", label: "Empleados", icon: Users },
    { to: "/rrhh/calendario", label: "Calendario", icon: CalendarDays },
    { to: "/rrhh/historial", label: "Historial de cambios", icon: History },
    { to: "/rrhh/exportar", label: "Exportar", icon: Download },
  ],
  liquidacion: [
    { to: "/liquidacion/resumen", label: "Liquidación del mes", icon: Calculator },
    { to: "/liquidacion/ajustes", label: "Ajustes", icon: SlidersHorizontal },
    { to: "/liquidacion/cierres", label: "Cierres", icon: Archive },
    { to: "/liquidacion/calendario", label: "Calendario", icon: CalendarDays },
    { to: "/liquidacion/hoy", label: "Hoy", icon: LayoutDashboard },
    { to: "/liquidacion/fichadas", label: "Fichadas", icon: Fingerprint },
    { to: "/liquidacion/justificativos", label: "Justificativos", icon: FileCheck2 },
    { to: "/liquidacion/empleados", label: "Empleados", icon: Users },
    { to: "/liquidacion/historial", label: "Historial de cambios", icon: History },
  ],
};

const ROL_LABEL = { rrhh: "Recursos Humanos", liquidacion: "Liquidación" };
const ROL_ROTULO = { rrhh: "RECURSOS HUMANOS", liquidacion: "LIQUIDACIÓN" };

export default function PanelLayout({ rol }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const ahora = useAhoraBA();
  const items = MENUS[rol];

  const { data: resumen } = useQuery({
    queryKey: ["alertas-resumen"],
    queryFn: async () => (await api.get("/alertas/resumen")).data,
    refetchInterval: 5000,
  });
  const { data: reloj } = useQuery({
    queryKey: ["demo-reloj-estado"],
    queryFn: async () => (await api.get("/demo/reloj")).data,
    refetchInterval: 10000,
  });
  const abiertas = resumen?.abiertas ?? 0;

  const salir = async () => {
    await logout();
    navigate("/login");
  };

  const linkCls = ({ isActive }) =>
    `group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors duration-150 ${
      isActive
        ? "bg-[#2DD4BF]/10 text-[#2DD4BF] shadow-[inset_2px_0_0_#2DD4BF]"
        : "text-gray-400 hover:bg-white/5 hover:text-white"
    }`;

  return (
    <div className="min-h-screen bg-background" data-testid={`panel-${rol}`}>
      <header className="fixed inset-x-0 top-0 z-30 flex h-16 items-center gap-4 border-b border-white/8 bg-[#0B0F14]/90 px-5 backdrop-blur">
        <div className="flex items-center gap-3" data-testid="logo-panel">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#2DD4BF] text-base font-black text-[#0B0F14]">
            C
          </span>
          <div className="leading-tight">
            <p className="text-[13px] font-extrabold tracking-[0.12em] text-[#2DD4BF]">CUERO &amp; CO.</p>
            <p className="text-[12px] font-semibold text-white">{rol === "liquidacion" ? "Liquidación" : "Asistencia"}</p>
          </div>
        </div>
        {reloj?.activo && (
          <button
            onClick={() => rol === "rrhh" && navigate("/demo-reloj")}
            className="ml-3 flex items-center gap-1.5 rounded-full bg-amber-400/15 px-3 py-1 text-[11px] font-semibold text-amber-300"
            data-testid="reloj-demo-aviso"
          >
            <Clock3 size={12} /> Reloj de demo activo
          </button>
        )}
        <div className="ml-auto flex items-center gap-6">
          <p className="hidden text-sm text-gray-300 lg:block" data-testid="fecha-larga">{fechaLargaBA(ahora)}</p>
          <div className="text-right leading-tight">
            <p className="font-mono-num text-lg font-semibold tabular-nums text-white" data-testid="hora-portal">
              {horaBA(ahora, false)}
            </p>
            <p className="text-[10px] uppercase tracking-[0.14em] text-gray-500">hora del portal</p>
          </div>
        </div>
      </header>

      <aside className="fixed bottom-0 left-0 top-16 z-20 hidden w-64 flex-col border-r border-white/8 bg-[#0B0F14] md:flex">
        <p className="px-5 pb-2 pt-6 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">
          {ROL_ROTULO[rol]}
        </p>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2" data-testid="menu-lateral">
          {items.map(({ to, label, icon: Icon, badge }) => (
            <NavLink key={to} to={to} className={linkCls} data-testid={`menu-${label.toLowerCase().replaceAll(" ", "-")}`}>
              <Icon size={17} className="shrink-0" />
              <span className="flex-1">{label}</span>
              {badge && abiertas > 0 && (
                <span className="rounded-full bg-red-400/15 px-2 py-0.5 text-[11px] font-bold text-red-300" data-testid="menu-alertas-badge">
                  {abiertas}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="space-y-2 border-t border-white/8 p-3">
          {rol === "rrhh" && (
            <NavLink
              to="/demo-reloj"
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-xl px-3 py-2 text-xs font-medium transition-colors ${
                  isActive ? "bg-[#2DD4BF]/10 text-[#2DD4BF]" : "text-gray-500 hover:text-white"
                }`
              }
              data-testid="menu-demo-reloj"
            >
              <Clock3 size={13} /> Reloj de demo
            </NavLink>
          )}
          <div className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#151A21] p-3" data-testid="usuario-actual">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#2DD4BF]/15 text-xs font-bold text-[#2DD4BF]">
              {user?.usuario?.slice(0, 1).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-sm font-semibold text-white">{user?.usuario}</p>
              <p className="text-[11px] text-gray-500">{ROL_LABEL[rol]}</p>
            </div>
            <button
              onClick={salir}
              title="Salir"
              className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white/5 hover:text-red-300"
              data-testid="boton-salir"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex min-h-screen flex-col pt-16 md:pl-64">
        <nav className="flex gap-2 overflow-x-auto border-b border-white/8 px-4 py-2.5 md:hidden" data-testid="menu-mobile">
          {items.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium ${
                  isActive ? "bg-[#2DD4BF] text-black" : "bg-[#151A21] text-gray-400"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 px-5 py-6 md:px-8 md:py-7">
          <Outlet context={{ relojDemo: reloj }} />
        </main>
      </div>
    </div>
  );
}
